import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import {
  Security as SecurityIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Login as LoginIcon,
  Devices as DevicesIcon,
  VpnKey as VpnKeyIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { AuthService } from '../../services/AuthService';

interface SecurityEvent {
  id: string;
  type: 'login' | 'logout' | 'password_change' | 'mfa_setup' | 'failed_login' | 'session_terminated';
  description: string;
  ip_address: string;
  user_agent: string;
  location?: string;
  timestamp: string;
  severity: 'low' | 'medium' | 'high';
}

interface SecurityRecommendation {
  id: string;
  title: string;
  description: string;
  action: string;
  priority: 'low' | 'medium' | 'high';
  completed: boolean;
}

interface ActiveSession {
  id: string;
  device_info: string;
  ip_address: string;
  location?: string;
  last_activity: string;
  is_current: boolean;
}

const SecurityDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [recommendations, setRecommendations] = useState<SecurityRecommendation[]>([]);
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
  const [securityScore, setSecurityScore] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // In a real app, these would be separate API calls
      // For now, we'll generate mock data
      await loadMockSecurityData();
    } catch (err: any) {
      setError(err.message || 'Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const loadMockSecurityData = async () => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const mockEvents: SecurityEvent[] = [
      {
        id: '1',
        type: 'login',
        description: 'Successful login',
        ip_address: '192.168.1.100',
        user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
        location: 'New York, NY',
        timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
        severity: 'low'
      },
      {
        id: '2',
        type: 'password_change',
        description: 'Password changed successfully',
        ip_address: '192.168.1.100',
        user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
        location: 'New York, NY',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
        severity: 'medium'
      },
      {
        id: '3',
        type: 'failed_login',
        description: 'Failed login attempt',
        ip_address: '203.0.113.45',
        user_agent: 'curl/7.68.0',
        location: 'Unknown',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(), // 2 days ago
        severity: 'high'
      }
    ];

    const mockRecommendations: SecurityRecommendation[] = [
      {
        id: '1',
        title: 'Enable Two-Factor Authentication',
        description: 'Add an extra layer of security to your account by enabling 2FA.',
        action: 'Setup MFA',
        priority: 'high',
        completed: false
      },
      {
        id: '2',
        title: 'Review Active Sessions',
        description: 'Regularly review and terminate unused sessions.',
        action: 'Review Sessions',
        priority: 'medium',
        completed: false
      },
      {
        id: '3',
        title: 'Update Password',
        description: 'Your password was last changed over 90 days ago.',
        action: 'Change Password',
        priority: 'medium',
        completed: false
      }
    ];

    const mockSessions: ActiveSession[] = [
      {
        id: '1',
        device_info: 'Windows 10 - Chrome 118.0',
        ip_address: '192.168.1.100',
        location: 'New York, NY',
        last_activity: new Date().toISOString(),
        is_current: true
      },
      {
        id: '2',
        device_info: 'iPhone 15 - Safari 17.0',
        ip_address: '192.168.1.101',
        location: 'New York, NY',
        last_activity: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
        is_current: false
      }
    ];

    setSecurityEvents(mockEvents);
    setRecommendations(mockRecommendations);
    setActiveSessions(mockSessions);
    setSecurityScore(75); // Calculate based on recommendations
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSecurityData();
    setRefreshing(false);
  };

  const handleTerminateSession = async (sessionId: string) => {
    try {
      // In a real app, this would call the API
      setActiveSessions(prev => prev.filter(session => session.id !== sessionId));
    } catch (err: any) {
      setError(err.message || 'Failed to terminate session');
    }
  };

  const getSeverityColor = (severity: 'low' | 'medium' | 'high') => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: 'low' | 'medium' | 'high') => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getEventIcon = (type: SecurityEvent['type']) => {
    switch (type) {
      case 'login': return <LoginIcon />;
      case 'logout': return <LoginIcon />;
      case 'password_change': return <VpnKeyIcon />;
      case 'mfa_setup': return <ShieldIcon />;
      case 'failed_login': return <WarningIcon />;
      case 'session_terminated': return <DevicesIcon />;
      default: return <HistoryIcon />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading security dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <SecurityIcon sx={{ mr: 2 }} />
          Security Dashboard
        </Typography>
        <Button
          variant="outlined"
          onClick={handleRefresh}
          disabled={refreshing}
          startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Security Score Overview */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
              <CircularProgress
                variant="determinate"
                value={securityScore}
                size={80}
                thickness={4}
                color={securityScore >= 80 ? 'success' : securityScore >= 60 ? 'warning' : 'error'}
              />
              <Box sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Typography variant="h6" component="div">
                  {securityScore}%
                </Typography>
              </Box>
            </Box>
            <Typography variant="h6" gutterBottom>
              Security Score
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {securityScore >= 80 ? 'Excellent' : securityScore >= 60 ? 'Good' : 'Needs Improvement'}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <DevicesIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              Active Sessions
            </Typography>
            <Typography variant="h4" color="primary">
              {activeSessions.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Devices logged in
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <HistoryIcon sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              Recent Events
            </Typography>
            <Typography variant="h4" color="info.main">
              {securityEvents.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last 7 days
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Security Recommendations */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <ShieldIcon sx={{ mr: 1 }} />
          Security Recommendations
        </Typography>
        
        {recommendations.length === 0 ? (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            Great! You've completed all security recommendations.
          </Alert>
        ) : (
          <Box sx={{ mt: 2 }}>
            {recommendations.map((recommendation) => (
              <Alert
                key={recommendation.id}
                severity={getPriorityColor(recommendation.priority) as any}
                sx={{ mb: 2 }}
                action={
                  <Button size="small" variant="outlined">
                    {recommendation.action}
                  </Button>
                }
              >
                <Typography variant="subtitle2" gutterBottom>
                  {recommendation.title}
                </Typography>
                <Typography variant="body2">
                  {recommendation.description}
                </Typography>
              </Alert>
            ))}
          </Box>
        )}
      </Paper>

      {/* Active Sessions */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <DevicesIcon sx={{ mr: 1 }} />
          Active Sessions
        </Typography>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Device</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeSessions.map((session) => (
                <TableRow key={session.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DevicesIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      {session.device_info}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {session.location || 'Unknown'}
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      {session.ip_address}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {format(new Date(session.last_activity), 'MMM dd, yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={session.is_current ? 'Current' : 'Active'}
                      color={session.is_current ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {!session.is_current && (
                      <Tooltip title="Terminate Session">
                        <IconButton
                          onClick={() => handleTerminateSession(session.id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Security Events */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ mr: 1 }} />
          Recent Security Events
        </Typography>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Event</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Severity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {securityEvents.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getEventIcon(event.type)}
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body2">
                          {event.description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {event.user_agent}
                      <br />
                      {event.ip_address}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {event.location || 'Unknown'}
                  </TableCell>
                  <TableCell>
                    {format(new Date(event.timestamp), 'MMM dd, yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={event.severity.toUpperCase()}
                      color={getSeverityColor(event.severity) as any}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default SecurityDashboard;